import { getAuthMe, useUpdateMyPhone } from "@features/auth/api";
import type { AdminUser } from "@features/users/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { getStoredUser, getStoredUserEmail, persistAuthUser } from "@shared/userStorage";
import { App as AntdApp, Button, Form, Input, Modal, Typography } from "antd";

interface Props {
  open: boolean;
}

/** Извлекает цифры из строки, проверяет что их 10-15. */
const isValidPhone = (value: string): boolean => {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
};

const PhoneOnboardingModal = ({ open }: Props) => {
  const { message } = AntdApp.useApp();
  const updatePhone = useUpdateMyPhone();
  const [form] = Form.useForm<{ phone: string }>();

  const applyPhoneToStoredUser = (phone: string) => {
    const stored = getStoredUser();
    if (!stored) return;
    const value = phone.trim();
    const existing = Array.isArray(stored.contacts) ? stored.contacts : [];
    const storedEmail = getStoredUserEmail();
    let hasPhone = false;
    let hasEmail = false;
    const nextContacts = existing.map((contact) => {
      if (contact.type !== "phone") return contact;
      hasPhone = true;
      return {
        ...contact,
        value,
        is_primary: Boolean(contact.is_primary),
      };
    });
    for (const contact of nextContacts) {
      if (contact.type === "email") {
        hasEmail = true;
        break;
      }
    }
    if (!hasEmail && storedEmail) {
      nextContacts.push({ type: "email", value: storedEmail, is_primary: true });
    }
    if (!hasPhone) {
      nextContacts.push({ type: "phone", value, is_primary: true });
    }
    persistAuthUser({ ...stored, contacts: nextContacts });
  };

  const refreshUserFromAPI = async () => {
    try {
      const data = (await getAuthMe()) as { user?: AdminUser };
      if (data?.user) {
        persistAuthUser(data.user);
      }
    } catch {
      // Оставляем локальные данные, если запрос не удался.
    }
  };

  const handleSubmit = (values: { phone: string }) => {
    updatePhone.mutate(values.phone.trim(), {
      onSuccess: () => {
        message.success("Телефон сохранён");
        applyPhoneToStoredUser(values.phone);
        void refreshUserFromAPI();
        form.resetFields();
      },
      onError: (err: unknown) => {
        message.error(extractErrorMessage(err));
      },
    });
  };

  return (
    <Modal title="Укажите номер телефона" open={open} closable={false} footer={null}>
      <Typography.Paragraph type="secondary">
        Номер телефона нужен для связи и восстановления доступа к аккаунту.
      </Typography.Paragraph>
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="phone"
          rules={[
            { required: true, message: "Введите номер телефона" },
            {
              validator: (_, value) =>
                value && isValidPhone(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error("Некорректный номер (10-15 цифр)")),
            },
          ]}
        >
          <Input placeholder="+7 900 123 45 67" size="large" />
        </Form.Item>
        <Button type="primary" htmlType="submit" loading={updatePhone.isPending} block>
          Сохранить
        </Button>
      </Form>
    </Modal>
  );
};

export default PhoneOnboardingModal;
